import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../auth/AuthContext";
import { Button } from "../components/ui/button";
import { ArrowRight, DollarSign, Heart, Sparkles, Users, Mountain, Flame, Loader2, Eye } from "lucide-react";
import { track } from "../lib/analytics";

const PILLAR_META = {
  financial: { icon: DollarSign, color: "#3e6b52", bg: "#e9f0ec", label: "Financial" },
  health: { icon: Heart, color: "#b94e4e", bg: "#f5e6e6", label: "Health" },
  spirituality: { icon: Sparkles, color: "#7a5fb0", bg: "#ede8f5", label: "Spirituality" },
  relationships: { icon: Users, color: "#c18848", bg: "#f5ece0", label: "Relationships" },
  endgame: { icon: Mountain, color: "#486a8f", bg: "#e7edf5", label: "Endgame" },
};

export default function SharedRoadmapPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = "A roadmap on Ascend Higher";
    (async () => {
      try {
        const r = await axios.get(`${API}/share/roadmap/${token}`);
        setData(r.data);
        track("shared_roadmap_viewed", { token });
      } catch (e) {
        setError("This link isn't available anymore.");
      }
    })();
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#f7f3ea] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <h1 className="font-display text-[34px] text-[#0f1724] mb-3">Link expired</h1>
          <p className="font-body text-[#4b5563] mb-6">{error}</p>
          <Link to="/">
            <Button className="h-12 px-6 rounded-full bg-[#486a8f] hover:bg-[#3a5a7c] text-white">
              Build your own roadmap
            </Button>
          </Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-[#f7f3ea] flex items-center justify-center">
        <Loader2 size={32} className="text-[#486a8f] animate-spin" />
      </div>
    );
  }

  const pillarKeys = Object.keys(PILLAR_META).filter(
    (k) => data.pillars && Object.keys(data.pillars[k] || {}).length > 0
  );

  return (
    <div className="min-h-screen bg-[#f7f3ea]" data-testid="shared-roadmap-page">
      <header className="sticky top-0 z-40 bg-[#f7f3ea]/85 backdrop-blur-md border-b border-[#e9e0cd]">
        <div className="max-w-5xl mx-auto px-6 h-[68px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-1">
            <span className="font-display text-[20px] font-semibold text-[#0f1724]">ascend</span>
            <span className="font-display text-[20px] font-semibold text-[#486a8f]">higher</span>
          </Link>
          <Link to="/start">
            <Button
              data-testid="shared-roadmap-cta-header"
              onClick={() => track("shared_roadmap_cta_click", { spot: "header" })}
              className="h-9 px-4 rounded-full bg-[#486a8f] hover:bg-[#3a5a7c] text-white text-[13px] font-medium"
            >
              Get yours <ArrowRight size={13} className="ml-1.5" />
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 md:py-16">
        {/* Card */}
        <div
          id="shareable-card"
          className="rounded-3xl border border-[#eadfc9] overflow-hidden shadow-[0_30px_80px_-40px_rgba(15,23,36,0.3)]"
        >
          <div
            className="p-10 md:p-14 text-white"
            style={{
              background:
                "radial-gradient(circle at 25% 20%, #1c2944 0%, #0f1724 55%, #050912 100%)",
            }}
          >
            <p className="font-mono-eyebrow text-[#8aa7c4] mb-3">A LIFE ROADMAP</p>
            <h1 className="font-display text-[40px] md:text-[56px] leading-[1.05] tracking-tight">
              {data.first_name}'s plan to{" "}
              <span className="italic font-light text-[#8aa7c4]">ascend higher.</span>
            </h1>

            {data.endgame_vision && (
              <p className="font-body text-[16px] md:text-[18px] text-white/85 mt-6 leading-relaxed max-w-xl">
                "{data.endgame_vision}"
              </p>
            )}

            <div className="mt-8 flex flex-wrap gap-3">
              {data.max_streak > 0 && (
                <span className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white/10 border border-white/15">
                  <Flame size={13} className="text-[#c18848]" />
                  <span className="font-body text-[12px]">
                    {data.max_streak}-day streak
                  </span>
                </span>
              )}
              {data.days_using > 0 && (
                <span className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white/10 border border-white/15">
                  <span className="font-body text-[12px]">
                    Day {data.days_using} on Ascend
                  </span>
                </span>
              )}
              <span className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white/10 border border-white/15">
                <Eye size={13} />
                <span className="font-body text-[12px]">{data.view_count} views</span>
              </span>
            </div>
          </div>

          <div className="p-8 md:p-12 bg-white">
            {/* Pillars */}
            {pillarKeys.length > 0 && (
              <div className="mb-10">
                <p className="font-mono-eyebrow text-[#486a8f] mb-4">FIVE PILLARS</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {pillarKeys.map((k) => {
                    const meta = PILLAR_META[k];
                    const Icon = meta.icon;
                    const summary = Object.values(data.pillars[k]).filter(Boolean).slice(0, 1).join(" ");
                    return (
                      <div key={k} className="flex items-start gap-3 rounded-xl bg-[#faf6ec] border border-[#f2ecdc] p-4">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: meta.bg }}>
                          <Icon size={15} style={{ color: meta.color }} />
                        </div>
                        <div className="min-w-0">
                          <p className="font-body text-[13px] font-medium text-[#0f1724]">{meta.label}</p>
                          <p className="font-body text-[12px] text-[#6b7280] line-clamp-2">{summary || "—"}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Horizons */}
            {data.horizons && data.horizons.length > 0 && (
              <div className="mb-10">
                <p className="font-mono-eyebrow text-[#486a8f] mb-4">HORIZONS</p>
                <ol className="space-y-3">
                  {data.horizons.map((h, i) => (
                    <li key={i} className="flex gap-3 rounded-xl border border-[#eadfc9] p-4">
                      <span className="font-display text-[20px] text-[#486a8f] w-6 shrink-0">{i + 1}</span>
                      <div>
                        <p className="font-body text-[14px] font-medium text-[#0f1724]">
                          {h.title || h.label || "Horizon"}
                        </p>
                        {h.summary && (
                          <p className="font-body text-[13px] text-[#4b5563] mt-1 leading-relaxed">{h.summary}</p>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {data.first_next_step && (
              <div className="rounded-2xl bg-[#e7edf5] border border-[#c5d2e3] p-5">
                <p className="font-mono-eyebrow text-[#486a8f] mb-1">TODAY'S STEP</p>
                <p className="font-body text-[14px] text-[#0f1724]">{data.first_next_step}</p>
              </div>
            )}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <h2 className="font-display text-[32px] md:text-[40px] text-[#0f1724] tracking-tight leading-tight">
            Build your own roadmap.
          </h2>
          <p className="font-body text-[15px] text-[#4b5563] mt-3 max-w-md mx-auto">
            Free for 3 days. Card required at Stripe checkout. AI life coach across goals, habits,
            health, money, relationships and endgame.
          </p>
          <Link to="/start">
            <Button
              data-testid="shared-roadmap-cta-footer"
              onClick={() => track("shared_roadmap_cta_click", { spot: "footer" })}
              className="mt-7 h-13 px-8 rounded-full bg-[#486a8f] hover:bg-[#3a5a7c] text-white font-medium text-[15px]"
            >
              Start free trial <ArrowRight size={15} className="ml-2" />
            </Button>
          </Link>
        </div>
      </main>
    </div>
  );
}
